<template>
  <div class="person">
    <FindBar
      v-if="isFindBarOpen"
      ref="findBarEl"
      container-id="scrollable-document"
      @close="closeFindBar"
    />
    <div id="scrollable-document">
      <PersonTopBar
        v-if="!embeddedMode"
        :person="value"
        :has-block-active="hasBlockActive"
        with-right-panel
        @select-all="selectAll"
        @flush="flush"
        @find="openFindBar"
      />
      <div class="infos" @keydown="keydown">
        <TitledDocument
          ref="document"
          :key="value.id"
          :debounce="debounce"
          :editable-title="false"
          :templates="templates"
          :value="value"
          :embedded-mode="embeddedMode"
        >
          <template #aboveTitle>
            <DefaultTypeChip v-if="!embeddedMode" kind="people" />
          </template>

          <template #properties>
            <ExtendedProperties
              class="extension-field-list"
              objectType="people"
              :objectId="value.id"
              :extensionObjectId="value.extension_row"
              :create-extension-object-callback="ensureExtensionObject"
            />
          </template>
        </TitledDocument>
      </div>
      <div class="sidebar">
        <div class="content">
          <Avatar :personish="{ kind: 'person', ...value }" size="big" />
          <div class="emails group">
            <ul>
              <li class="first">
                <EmailClickable :email="email" />
              </li>
              <div v-if="emails">
                <li v-for="otherEmail in emails" :key="otherEmail">
                  <EmailClickable :email="otherEmail" />
                </li>
              </div>
            </ul>
            <div class="emails side">
              <EmailIcon class="icon" />
              <div class="bar" />
            </div>
          </div>
          <div v-if="organizations" class="organizations group">
            <ul>
              <li
                v-for="organization in organizations"
                :key="`${organization.name}${organization.job_title || ''}${
                  organization.current || ''
                }`"
              >
                <span class="name">{{ organization.name }}</span>
                <span v-if="organization.job_title"
                  >({{ organization.job_title }})</span
                >
              </li>
            </ul>
            <div class="organizations side">
              <OrganizationIcon class="icon" />
              <div class="bar" />
            </div>
          </div>
          <div v-if="phones" class="phones group">
            <ul>
              <li v-for="phone in phones" :key="phone">
                {{ phone }}
              </li>
            </ul>
            <div class="phones side">
              <PhoneIcon class="icon" />
              <div class="bar" />
            </div>
          </div>
          <div v-if="value.address" class="addresses group">
            <ul>
              <li>
                <a
                  :href="`https://www.google.com/maps/search/${value.address}`"
                  target="_blank"
                  >{{ value.address }}</a
                >
              </li>
            </ul>
            <div class="addresses side">
              <LocationIcon class="icon" />
              <div class="bar" />
            </div>
          </div>
          <EventsList
            v-if="previousEvents?.length > 0"
            class="events group"
            :events="previousEvents"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { useHead } from "@unhead/vue";
import { computedAsync } from "@vueuse/core";
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  PropType,
  provide,
  ref,
  toRefs,
} from "vue";

import { People } from "@ctrl/controller-rpc";

import { useDocument } from "@/components/document";
import Avatar from "@/components/elements/Avatar.vue";
import DefaultTypeChip from "@/components/elements/DefaultTypeChip.vue";
import EmailClickable from "@/components/elements/EmailClickable.vue";
import EventsList from "@/components/elements/EventsList.vue";
import FindBar from "@/components/find/FindBar.vue";
import { useFindBar } from "@/components/find/useFindBar";
import EmailIcon from "@/components/icons/EmailIcon.vue";
import LocationIcon from "@/components/icons/LocationIcon.vue";
import OrganizationIcon from "@/components/icons/OrganizationIcon.vue";
import PhoneIcon from "@/components/icons/PhoneIcon.vue";
import * as inject from "@/components/inject";
import PersonTopBar from "@/components/person/PersonTopBar.vue";
import TitledDocument from "@/components/TitledDocument.vue";
import { useExtendedProperties } from "@/composables/useExtendedProperties";
import { useScrollable } from "@/composables/useScrollable";
import { keydown } from "@/routes/object";
import type { PeopleWithTitle } from "@/routes/Person.vue";

import ExtendedProperties from "../ExtendedProperties.vue";
import { TemplateName } from "../templates";

export default defineComponent({
  components: {
    PersonTopBar,
    Avatar,
    DefaultTypeChip,
    EmailClickable,
    EmailIcon,
    ExtendedProperties,
    EventsList,
    FindBar,
    LocationIcon,
    OrganizationIcon,
    PhoneIcon,
    TitledDocument,
  },
  props: {
    debounce: {
      type: Number,
      default: 5000,
    },
    previousRoute: {
      type: String,
      default: null,
    },
    value: {
      type: Object as PropType<PeopleWithTitle>,
      required: true,
    },
    embeddedMode: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const router = inject.router();
    const controller = inject.controller();
    const profile = inject.profile();

    // Register the document element as a scrollable container
    const scrollable = useScrollable(["scrollable-document"]);
    onMounted(scrollable.mount);
    onBeforeUnmount(scrollable.unmount);

    const { value } = toRefs(props);
    // FIXME: Mandatory due to BaseTopBar teleportation
    const isMounted = ref(false);
    onMounted(() => (isMounted.value = true));
    const document = ref<InstanceType<typeof TitledDocument> | null>(null);
    const parent = ref({ kind: "people", id: value.value.id });

    const { isFindBarOpen, findBarEl, openFindBar, closeFindBar } =
      useFindBar();
    provide("parent", parent);

    const tint = computed(() => (value.value.archived ? "red" : "none"));
    const email = computed(() => {
      return value.value.email;
    });

    const ensureExtensionObject = useExtendedProperties(
      controller,
      profile,
      "people",
      () => value.value.extension_row,
      (id: string) => {
        if (value.value) {
          value.value.extension_row = id;
        }
      },
      async (id: string) => {
        await controller.people.patch(
          value.value.id,
          { extension_row: id },
          "app"
        );
      }
    );
    const emails = computed(() => {
      return value.value.otherEmails.length > 0
        ? value.value.otherEmails
        : null;
    });
    const initials = computed(() => {
      const name = value.value.name;
      if (name?.given && name?.family) {
        const givenNameInitial = name?.given.charAt(0).toUpperCase();
        const familyNameInitial = name?.family.charAt(0).toUpperCase();
        return `${givenNameInitial}${familyNameInitial}`;
      }
      return email.value.charAt(0).toUpperCase();
    });
    const organizations = computed(() => {
      return value.value.organizations.length > 0
        ? value.value.organizations
        : null;
    });
    const phones = computed(() => {
      return value.value.phones.length > 0 ? value.value.phones : null;
    });
    const photo = computed(() => {
      return value.value.photo;
    });
    const templates = computed<TemplateName[]>(() => {
      return ["empty", "OneOnOneTopics"];
    });
    const notes = computed(() => {
      return value.value.notes;
    });
    const previousEvents = computedAsync(async () => {
      if (!email.value) return [];
      const emails = [email.value]; // Backend is going to search for all identities + these emails.
      return (await controller.event.findWithAttendees(emails)).filter(
        (event) => event.start.getTime() < Date.now()
      );
    });
    useHead({
      title: computed(() => value.value.name?.display || value.value.email),
    });
    return {
      closeFindBar,
      document,
      email,
      emails,
      ensureExtensionObject,
      findBarEl,
      isFindBarOpen,
      openFindBar,
      initials,
      isMounted,
      keydown,
      notes,
      open,
      organizations,
      phones,
      photo,
      previousEvents,
      templates,
      tint,
      ...useDocument<People>(value, document, router),
    };
  },
});
</script>

<style scoped lang="scss">
.rtext {
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: 8px;

  .fill {
    @include themed() {
      fill: t($secondary);
    }
  }

  .stroke {
    @include themed() {
      stroke: t($secondary);
    }
  }

  @include themed() {
    color: t($secondary);
  }
}

div.person {
  // Anchor for the find bar.
  position: relative;
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
  padding: 0;
  cursor: default;

  // Clear the fixed 330px sidebar.
  .find-bar {
    inset-inline-end: 350px;
  }

  #scrollable-document {
    width: 100%;
    box-sizing: border-box;
    display: flex;
    overflow-y: scroll;
    scrollbar-width: none;
  }

  .infos {
    width: calc(100% - 330px);
    box-sizing: border-box;
    padding: 16px 0;
    height: 100%;
    max-width: 1500px;
    display: flex;
    flex-direction: column;
    align-items: center;

    .doc {
      height: 100%;
      max-width: 1500px;
      margin: 16px auto 0;
      width: calc(100% - 128px);
      padding: 0 64px;
      display: flex;
      flex-direction: column;
      align-items: center;

      h1 {
        width: 100%;
        max-width: $document-max-width;
        padding-top: 30px;
        @extend %font-h1;
      }
    }
  }

  .sidebar {
    position: fixed;
    inset-inline-end: 0;
    width: 330px;
    height: 100%;
    @extend %font-caption-regular;
    @include themed() {
      background: t($bg-alt);
      border-inline-start: 1px solid t($grey-10);
      color: t($grey-60);
    }

    .content {
      height: 100%;
      padding: 24px 24px 0 24px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      overflow-x: hidden;
      overflow-y: scrollbar;
      -ms-overflow-style: none; /* IE and Edge */
      scrollbar-width: none; /* Firefox */
      &::-webkit-scrollbar {
        display: none;
      }

      li {
        padding-block: 5px;
        padding-inline-start: 12px;
      }

      .avatar {
        margin-bottom: 24px;
      }
      .emails li.first,
      .phones li:first-child,
      .organizations li .name {
        @include themed() {
          color: t($primary);
        }
      }

      .emails {
        :deep(li:not(.first)) a {
          @include themed() {
            color: t($grey-60);
          }
        }

        li {
          letter-spacing: 0.3px;
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
        }
      }

      .organizations {
        line-height: 19px;
        li {
          display: flex;
          flex-direction: row;
          column-gap: 4px;
        }
      }

      .addresses {
        line-height: 23px;

        a {
          text-decoration: none;
          @include themed() {
            color: t($primary);
          }
        }

        .icon {
          margin-top: 4px;
        }
      }

      .group {
        width: 100%;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        margin-bottom: 24px;

        ul {
          flex-shrink: 0;
          width: calc(100% - 42px);
          padding: 0;
          margin: 0;
        }
      }

      .side {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: center;
        margin: 4px 0 0 0;
        width: 42px;
        height: 100%;
        .icon {
          flex-shrink: 0;

          @include themed() {
            color: t($grey-40);
          }
        }

        .bar {
          border: none;
          height: 100%;
          width: 1px;
          border-radius: 10px;
          margin: 6px 0 6px 0;

          @include themed() {
            background: t($grey-15);
          }
        }
      }
    }
  }
}
</style>
